<template>
  <div class="pts-skeleton-container">
    <div class="pts-skeleton-container-header">
      <Skeleton height="20px" width="150px" borderRadius="4px" />
      <Skeleton height="24px" width="141px" borderRadius="4px" />
    </div>

    <div class="pts-skeleton-container-main">
      <div class="pts-skeleton-container-main-top">
        <div class="pts-skeleton-container-main-top-inner">
          <div class="pts-skeleton-container-main-top-inner-left">
            <Skeleton height="18px" width="95px" borderRadius="4px" />
          </div>

          <div class="pts-skeleton-container-main-top-inner-right">
            <div
              v-for="b in topBlocks"
              :key="b.key"
              class="pts-top-block"
              :style="{ width: b.w, paddingBottom: b.pb || undefined }"
            >
              <Skeleton height="20px" width="20px" borderRadius="16px" />

              <div class="pts-top-block-right">
                <Skeleton
                  v-for="(l, idx) in b.lines"
                  :key="idx"
                  :height="l.h"
                  :width="l.w"
                  borderRadius="4px"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pts-skeleton-container-main-bottom">
        <div class="pts-table-row pts-table-row--first">
          <div class="pts-table-cell pts-table-cell--r1c1">
            <div class="pts-table-cell-inner">
              <Skeleton height="18px" width="100px" borderRadius="4px" />
            </div>
          </div>

          <div class="pts-table-row-rest pts-table-row-rest--first">
            <div
              v-for="c in row1"
              :key="c.key"
              class="pts-table-cell"
              :class="`pts-table-cell--${c.key}`"
            >
              <div
                class="pts-table-cell-inner"
                :class="{ 'pts-table-cell-inner--r1c6': c.key === 'r1c6' }"
              >
                <Skeleton
                  v-for="(s, i) in c.skeletons"
                  :key="i"
                  :height="s.h"
                  :width="s.w"
                  :borderRadius="s.r"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="pts-table-row pts-table-row--second">
          <div class="pts-table-cell pts-table-cell--r2c1">
            <div class="pts-table-cell-inner">
              <Skeleton height="36px" width="116px" borderRadius="4px" />
            </div>
          </div>

          <div class="pts-table-row-rest pts-table-row-rest--second">
            <div
              v-for="c in row2"
              :key="c.key"
              class="pts-table-cell"
              :class="`pts-table-cell--${c.key}`"
            >
              <div class="pts-table-cell-inner">
                <Skeleton
                  v-for="(s, i) in c.skeletons"
                  :key="i"
                  :height="s.h"
                  :width="s.w"
                  :borderRadius="s.r"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="pts-skeleton-container-footer">
      <div class="pts-skeleton-container-footer-left">
        <Skeleton width="148px" height="40px" borderRadius="4px" />
      </div>

      <div class="pts-skeleton-container-footer-right">
        <div class="pts-skeleton-container-footer-right-leftSide">
          <Skeleton width="20px" height="20px" borderRadius="4px" />
          <Skeleton width="206px" height="16px" borderRadius="4px" />
        </div>

        <div class="pts-skeleton-container-footer-right-rightSide">
          <Skeleton width="183px" height="40px" borderRadius="4px" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Skeleton from '@/components/Skeleton/Skeleton';

const sk = (h, w, r = '4px') => ({ h, w, r });

const topBlocks = [
  { key: 'first',
w: '58px',
pb: '10px', lines: [sk('16px', '30px')] },
  {
    key: 'second',
    w: '107px',
    lines: [sk('16px', '40px'), sk('12px', '78px')],
  },
  { key: 'third',
w: '106px', lines: [sk('16px', '40px'), sk('12px', '78px')] },
];

const row1 = [
  ...['r1c2', 'r1c3', 'r1c4', 'r1c5'].map((key) => ({
    key,
    skeletons: [sk('18px', '70px')],
  })),
  { key: 'r1c6', skeletons: [sk('18px', '70px'), sk('20px', '20px', '16px')] },
  ...['r1c7', 'r1c8'].map((key) => ({ key, skeletons: [sk('18px', '70px')] })),
];

const row2 = [
  ...['r2c2', 'r2c3', 'r2c4'].map((key) => ({
    key,
    skeletons: [sk('18px', '82px')],
  })),
  { key: 'r2c5', skeletons: [sk('18px', '117px')] },
  ...['r2c6', 'r2c7', 'r2c8'].map((key) => ({
    key,
    skeletons: [sk('40px', '122px')],
  })),
];
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.pts-skeleton-container {
  display: flex;
  width: 1034px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid $colorD20;
  background: $colorN00;

  &-header {
    display: flex;
    padding: 24px 24px 16px 24px;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    border-bottom: 0.5px solid $colorD20;
  }

  &-main {
    display: flex;
    padding: 16px 24px 0 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    align-self: stretch;

    &-top {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      align-self: stretch;

      &-inner {
        display: flex;
        align-items: flex-start;
        gap: 24px;

        &-right {
          display: flex;
          align-items: center;
          gap: 36px;
        }
      }
    }

    .pts-top-block {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .pts-top-block-right {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 2px;
    }

    .pts-skeleton-container-main-bottom {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      border-radius: 8px;
      border: 1px solid $colorD40;
      overflow: hidden;

      &,
      * {
        box-sizing: border-box;
      }
    }

    .pts-table-row {
      width: 100%;
      display: flex;
      align-self: stretch;
      position: relative;
    }

    .pts-table-row--first::after {
      content: '';
      position: absolute;
      left: 140px;
      right: 0;
      bottom: 0;
      height: 4px;
      background: $colorD20;
      pointer-events: none;
    }

    .pts-table-row--first::before {
      content: '';
      position: absolute;
      left: 140px;
      bottom: 0;
      width: 0.5px;
      height: 4px;
      background: $colorD30;
      pointer-events: none;
    }

    .pts-table-row-rest--first,
    .pts-table-row-rest--second {
      display: flex;
      flex: 1 1 auto;
      min-width: 0;
    }

    .pts-table-cell--r1c1,
    .pts-table-cell--r1c2,
    .pts-table-cell--r1c3,
    .pts-table-cell--r1c4,
    .pts-table-cell--r1c5,
    .pts-table-cell--r1c6,
    .pts-table-cell--r1c7,
    .pts-table-cell--r1c8 {
      display: flex;
      height: 50px;
      padding: 14px 8px 14px 12px;
      align-items: center;
      gap: 2px;
      border-right: 0.5px solid $colorD30;
      flex: 0 0 auto;
    }

    .pts-table-cell--r2c1,
    .pts-table-cell--r2c2,
    .pts-table-cell--r2c3,
    .pts-table-cell--r2c4,
    .pts-table-cell--r2c5,
    .pts-table-cell--r2c6,
    .pts-table-cell--r2c7,
    .pts-table-cell--r2c8 {
      display: flex;
      padding: 16px 12px;
      align-items: center;
      align-self: stretch;
      border-right: 0.5px solid $colorD30;
      flex: 0 0 auto;
    }

    .pts-table-cell--r1c1 {
      width: 140px;
      flex: 0 0 140px;
      border-bottom: 1px solid $colorD30;
    }
    .pts-table-cell--r1c2,
    .pts-table-cell--r1c3 {
      width: 106px;
      flex: 0 0 106px;
    }
    .pts-table-cell--r1c4 {
      width: 117px;
      flex: 0 0 117px;
    }
    .pts-table-cell--r1c5 {
      width: 141px;
      flex: 0 0 141px;
    }
    .pts-table-cell--r1c6 {
      width: 154px;
      flex: 0 0 154px;
      justify-content: space-between;
    }
    .pts-table-cell--r1c7 {
      width: 154px;
      flex: 0 0 154px;
    }

    .pts-table-cell--r1c8 {
      width: 117px;
      padding: 14px 12px;
      flex: 1 0 117px;
      min-width: 117px;
    }

    .pts-table-cell-inner--r1c6 {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
    }

    .pts-table-cell--r2c1 {
      width: 140px;
      flex: 0 0 140px;
    }
    .pts-table-cell--r2c2,
    .pts-table-cell--r2c3 {
      width: 106px;
      flex: 0 0 106px;
    }
    .pts-table-cell--r2c4 {
      width: 117px;
      flex: 0 0 117px;
    }
    .pts-table-cell--r2c5 {
      width: 141px;
      flex: 0 0 141px;
    }

    .pts-table-cell--r2c6,
    .pts-table-cell--r2c7 {
      width: 154px;
      flex: 0 0 154px;
      padding: 16px;
      gap: 10px;
    }

    .pts-table-cell--r2c8 {
      width: 117px;
      flex: 1 0 117px;
      min-width: 117px;
      padding: 16px;
      gap: 10px;
    }
  }

  &-footer {
    display: flex;
    width: 1034px;
    padding: 16px 24px 24px 24px;
    justify-content: space-between;
    align-items: center;

    &-right {
      display: flex;
      align-items: center;
      gap: 16px;
      align-self: stretch;

      &-leftSide {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
  }
}
</style>
